import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuoteWorksheetSwitch } from './useQuoteWorksheetSwitch';

interface Decision {
  /** The navigation (location.key) this decision is for. */
  key: string;
  /** When that navigation was first seen: a read that failed after it is this visit's answer. */
  since: number;
  /** null until decided. */
  worksheet: boolean | null;
}

/** Which screens a page belongs to: the worksheet itself, or the price-catalog screens (useQuoteWorksheetSwitch). */
export type WorksheetScreen = 'worksheet' | 'catalog';

/**
 * Which screen the page now open uses: true (the new screen), false (today's screens) or null (not decided: show a
 * skeleton). Used by every page that picks between the two (WorksheetPreviewGate, the Create Quote and View Quote
 * switches). `screen` picks the switch: the worksheet (default) or the price-catalog screens.
 *
 *   A cached OFF decides at once: off is today's screens, so there is nothing to wait for (#8450 red-team M3: with the
 *   switch off, Create Quote and View Quote must not wait on a read). A switch turned on since is picked up by the next
 *   read (30 s stale time, or the tab coming back into focus).
 *   A cached ON is decided on a FRESH read: a stale cached answer is not trusted. A route that mounts re-reads it by
 *   itself; a new navigation on a route already mounted (Cmd+K from quote A to quote B) asks for the re-read here (Q9
 *   delta-2). Either way it waits for the result: a tab that cached live:true before a rollback does not open the
 *   worksheet, and a re-read that fails decides off (today's screens). An answer read within the last 30 s counts as
 *   fresh, so a rollback reaches a tab that read the switch just before it within those 30 s.
 *   Decided ONCE per navigation (location.key): a later re-read (the tab back in focus after a flip or a rollback) never
 *   swaps the page someone is typing in. The next navigation, even to the same address, decides afresh.
 */
export function useWorksheetDecision(screen: WorksheetScreen = 'worksheet'): boolean | null {
  const location = useLocation();
  const s = useQuoteWorksheetSwitch();
  const visit = useRef<Decision | null>(null);
  if (visit.current?.key !== location.key) visit.current = { key: location.key, since: Date.now(), worksheet: null };
  const current = visit.current;
  const on = screen === 'catalog' ? s.catalog : s.worksheet;
  // Fresh: not stale, or this visit's own re-read failed (a failure is an answer: off).
  const fresh = !s.isStale || s.failedAt >= current.since;
  const reading = s.isLoading || s.isFetching || s.isRefreshing;
  if (current.worksheet === null && !s.isLoading && !on) {
    // Off (cached, failed, or not asked): today's screens, at once.
    visit.current = { ...current, worksheet: false };
  } else if (current.worksheet === null && !reading && fresh) {
    visit.current = { ...current, worksheet: on };
  }
  // A stale answer is re-read: waited on for a cached on, in the background for a cached off (decided already), so the next
  // navigation sees a switch turned on since.
  const needsRead = !reading && !fresh;
  const { refetch } = s;
  useEffect(() => {
    if (needsRead) void refetch();
  }, [needsRead, refetch, location.key]);
  return visit.current.worksheet;
}
